// @flow

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { rankWith, uiTypeIs } from '@jsonforms/core';

import {
    Box,
    Flex,
    Stack,
    Position,
    Heading,
    Loader,
    TextField,
    Alert,
    Icon,
    useToast
} from '@locus-taxy/neo-ui';
import useLayoutProps from '../util/useLayoutProps';
import { useRenewExpiry } from 'platform/settings/hooks/useRenewExpiry';
import { useMinimalClient } from 'platform/settings/hooks/useMinimalClient';
import MaraOnlyTag from 'platform/settings/form/renderers/components/MaraOnlyTag';
import { SaveWithReasonButton } from 'platform/settings/form/renderers/components/SaveWithReasonButton';
import { ApiStatusEnum } from 'common/enums/API';
import DateTimeUtils, { DateStyle } from 'common/utils/DateTimeUtils';

const RenewClientExpiryLayout = (props: Props): React.Node => {
    const { t } = useTranslation('platform');
    const renewExpiry = useRenewExpiry();
    const minimalClient = useMinimalClient();
    const { showToast } = useToast();

    const { uischema } = useLayoutProps(props);

    const onSave = (reason: string) => {
        const requestPayload = {
            reason
        };

        renewExpiry.mutate(requestPayload, {
            onSuccess: () => {
                showToast({
                    title: `Client renewed successfully.`,
                    palette: 'success',
                    icon: (
                        <Icon
                            icon="check_circle"
                            size="xLarge"
                            color="green.500"
                        />
                    )
                });
            },
            onError: error => {
                showToast({
                    title: error.message,
                    palette: 'danger',
                    icon: (
                        <Icon
                            icon="cross_circle"
                            size="xLarge"
                            color="red.600"
                        />
                    )
                });
            }
        });
    };

    if (minimalClient.status === ApiStatusEnum.Loading) {
        return <Loader />;
    }

    return (
        <Stack space="medium">
            <Heading size="large" color="grey.900">
                {t(uischema.label)}
            </Heading>
            <Box width="100%">
                <Stack space={uischema.options?.space || 'medium'}>
                    <Box>
                        <Alert
                            icon="error_outline"
                            palette="warning"
                            text={`${t(
                                'settings.platform.fields.clientSettings.fields.renewClientExpiry.bannerText.label'
                            )}
                            ${t(
                                // eslint-disable-next-line max-len
                                'settings.platform.fields.clientSettings.fields.renewClientExpiry.bannerText.description'
                            )}`}
                        />
                    </Box>
                    <Box>
                        <TextField
                            label={t(
                                'settings.platform.fields.clientSettings.fields.renewClientExpiry.expiresOn.label'
                            )}
                            fullWidth
                            disabled
                            value={DateTimeUtils.format(
                                minimalClient.data?.expiresOn,
                                { dateStyle: DateStyle.SHORT }
                            )}
                            onChange={() => {}}
                        />
                        <Box py="1">
                            <MaraOnlyTag />
                        </Box>
                    </Box>
                    <Position
                        position="absolute"
                        bottom="0"
                        right="0"
                        width="100%"
                    >
                        <Flex width="100%" p="4" flexDirection="row-reverse">
                            <SaveWithReasonButton
                                label={t('renew')}
                                showReasonModal
                                onSave={onSave}
                                disabled={
                                    !['DEMO', 'PILOT'].includes(
                                        minimalClient.data?.stage
                                    )
                                }
                                isSaving={
                                    renewExpiry.status === ApiStatusEnum.Loading
                                }
                            />
                        </Flex>
                    </Position>
                </Stack>
            </Box>
        </Stack>
    );
};

export default RenewClientExpiryLayout;

export const renewClientExpiryTester = rankWith(
    1,
    uiTypeIs('RenewClientExpiryLayout')
);
